import type { ReaderSettings } from "../../user/reader-settings.js";

/**
 * The Chinese an article writes about, in every writing a reader may choose.
 *
 * A 例句 carries its own alternatives, because `cli/liju-generate.mts` made
 * them and the page holds all three. The Chinese in an article's prose has
 * none: it is characters typed into a sentence of English, and nothing has ever
 * converted it. This is that conversion, precomputed against the site's own
 * vocabulary and small enough to travel with the page.
 *
 * **Words before characters**, which is the rule `@kensio/pinyinjs` converts by
 * and the reason this is two maps rather than one. 面 is 面 counting faces and
 * 麵 in 面包, and no character table can tell those apart. Only the words a
 * character table gets *wrong* are stored, so the word map is an exception list
 * rather than a dictionary of the site's Chinese.
 *
 * `cli/writings.mts` builds it, `src/site/writings.json` holds it and
 * `src/site/article-writing.ts` applies it in the browser. The same class does
 * the applying at build time, which is what lets the builder check its own
 * answer against the library it built the table from.
 */

/** The site's Chinese, keyed by the 简体 it was written in. */
export interface WritingFormsData {
  /**
   * One character apiece, for everything a word says nothing about.
   *
   * Only the characters that change. A run holding none of them comes out of a
   * conversion as it went in, which is most of the Chinese on this site: most
   * characters were untouched by simplification.
   */
  readonly characters: Readonly<Record<string, readonly string[]>>;

  /** The words whose characters, taken one at a time, would be wrong. */
  readonly words: Readonly<Record<string, readonly string[]>>;

  /**
   * The 國語 reading of a glossed word, where it is not the 普通话 one.
   *
   * Keyed by the 简体 characters the reading was written beside, and holding
   * the syllables as `@kensio/pinyinjs` marks them up. The reading standard
   * follows the writing (see `writingTags`), so a heading redrawn in 繁體
   * takes the 國語 reading with it, and 谁 (shéi) becomes 誰 (shuí).
   *
   * Almost always empty for a given word, which is why this is an exception
   * list too: the two standards agree on all but a few hundred words.
   */
  readonly readings: Readonly<Record<string, string>>;
}

/** A table holding nothing, which converts every text into itself. */
export const noWritingForms: WritingFormsData = {
  characters: {},
  words: {},
  readings: {},
};

/** What the forms are ordered by, and where a writing sits in that order. */
const writingOrder: Readonly<Record<ReaderSettings["writing"], number>> = {
  "zh-CN": 0,
  "zh-TW": 0,
  "zh-HK": 1,
};

/** What it takes to write the site's Chinese another way. */
export interface WritingFormsProperties {
  /** The table, as `src/site/writings.json` holds it. */
  readonly data: WritingFormsData;
}

/**
 * Writing the site's own Chinese in one of the writings a reader may choose.
 *
 * **Taiwan first and Hong Kong only where it differs**, which is the order
 * `hanziTags` puts the writings in and the rule `SentenceReader.distinct`
 * already follows for a sentence's lines. Hong Kong writes 説 for 說 and 台 for
 * 臺, and writes everything Taiwan writes for the other 20,000 characters, so a
 * form list of one serves both.
 *
 * 简体 is the writing everything here was authored in, so it takes the same
 * position as Taiwan and is never looked up: a reader on 简体 is a reader this
 * class is never asked about.
 */
export class WritingForms {
  private readonly data: WritingFormsData;

  /** How far back a longest match has to look. Nought where there are no words. */
  private readonly longestWord: number;

  constructor(properties: WritingFormsProperties) {
    this.data = properties.data;
    this.longestWord = Object.keys(properties.data.words).reduce(
      // `Array.from` rather than a spread, which counts by code point and
      // keeps a hanzi outside the basic plane whole. `HanziScript` says the
      // same at greater length.
      (longest, word) => Math.max(longest, Array.from(word).length),
      0,
    );
  }

  /**
   * The form a table entry takes in one writing.
   *
   * Hong Kong falls back to Taiwan's form, which is what a list of one means.
   */
  private formOf(
    forms: readonly string[] | undefined,
    at: number,
  ): string | undefined {
    return forms === undefined ? undefined : (forms[at] ?? forms[0]);
  }

  /** The longest word the table holds at this position, if it holds any. */
  private wordAt(
    characters: readonly string[],
    from: number,
  ): string | undefined {
    const most = Math.min(this.longestWord, characters.length - from);

    for (let length = most; length > 1; length -= 1) {
      const word = characters.slice(from, from + length).join("");

      if (this.data.words[word] !== undefined) {
        return word;
      }
    }

    return undefined;
  }

  /**
   * A text as the given writing writes it.
   *
   * Everything the table says nothing about comes through untouched, which is
   * the English around the characters as well as the characters both scripts
   * share. That is what makes this safe to run over a whole paragraph.
   */
  written(text: string, writing: ReaderSettings["writing"]): string {
    const at = writingOrder[writing];
    const characters = Array.from(text);
    let written = "";
    let from = 0;

    while (from < characters.length) {
      const word = this.wordAt(characters, from);

      if (word === undefined) {
        const character = characters[from]!;

        written +=
          this.formOf(this.data.characters[character], at) ?? character;
        from += 1;
      } else {
        written += this.formOf(this.data.words[word], at) ?? word;
        from += Array.from(word).length;
      }
    }

    return written;
  }

  /**
   * The 國語 reading of a glossed word, where it is not the 普通话 one.
   *
   * Undefined for everything else, which is nearly every word: a caller can
   * ask about any characters it finds a reading beside without checking first.
   */
  reading(hanzi: string): string | undefined {
    return this.data.readings[hanzi];
  }
}
